import { useState, useEffect, useRef } from 'react';
import { AnyAction } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import useDeepCompareEffect from 'use-deep-compare-effect';
import qs from 'query-string';
import type { Content } from '@plone/types';
import { findBlocks } from '@plone/volto/helpers/Blocks/Blocks';
import { slugify } from '@plone/volto/helpers/Utils/Utils';
import { searchContent } from '@plone/volto/actions';
import type { GetQueryStringResponse } from '@plone/types';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import {
  getAvailableIndexes,
  getSubrequest,
  availableIndexesClearSSR,
} from '@fhnw/actions/availableIndexes/availableIndexes';
// @ts-ignore
import hoistNonReactStatics from 'hoist-non-react-statics';
import {
  deepEqual,
  filterObject,
} from '@designsystem/stories/01-core/Utilities';
import { DropdownItem, DataMap } from '@designsystem/stories/02-atoms/Dropdown';
import { Filter, FilterProps } from '@designsystem/stories/03-molecules/Filter';
import { LogoProps } from '@designsystem/stories/02-atoms/Logo';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import filterSVG from '@fhnw/icons/filter.svg';
import config from '@plone/volto/registry';
import { messages } from './messages';
import { sortFilterValues, hasRelevanceTrigger } from './filterSorting';
import { listingPageSize } from './insertTeaserCard';
import {
  collapseActiveFilters,
  collapseFacetForDisplay,
  expandActiveFilters,
} from './mergedFilters';

/** Criteria whose value list carries a search field inside it (#1777). */
const ENABLE_SEARCH = [
  'organisation_entities',
  'topics',
  'research_areas',
  'specializations',
  'linked_products',
  'linked_degree_program',
  'linked_continuing_education',
  'lecturer',
  'contact',
];

/**
 * Criteria offered as a type-ahead search instead of a value list (#1777).
 *
 * Both pick individual pages through the UID index (#1651), so every value
 * matches exactly one page and a list of them would be no filter at all. Their
 * three siblings from #1651 - linked_products, lecturer and contact - are
 * facetted value lists like any other criterion.
 */
const TYPEAHEAD_FILTERS = [
  'linked_degree_program',
  'linked_continuing_education',
];

const GROUPED_FILTERS = ['topics'];

/**
 * @function useCreateQueryStringPostfix
 * @description A hook that creates a postfix with an id if there are multiple blocks with filters.
 */
const useCreateQueryStringPostfix = (id: string): string => {
  const blockTypesWithFilters = ['search', 'listing'];
  const blocks =
    useSelector(
      (state: { content: { data: Content } }) => state?.content?.data?.blocks,
    ) || [];
  const hasMultiple = findBlocks(blocks, blockTypesWithFilters).length > 1;

  return hasMultiple ? slugify(`-${id}`) : '';
};

function getDisplayName(WrappedComponent: React.ComponentType<any>) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

interface WithFilterProps {
  data: {
    querystring?: {
      query?: Array<{
        i: string;
        o: string;
        v?: string | string[];
      }>;
      b_size?: number | string;
      sort_on?: string;
      sort_order_boolean?: boolean;
    };
    activateFilter?: boolean;
    showTeaserCard?: boolean;
    filters?: Array<{
      title?: string;
      index: string;
    }>;
    allowLayoutSwitch?: boolean;
    sortSelection?: Array<{
      title?: string;
      index: string;
      reverse?: boolean;
    }>;
    variation?: string;
  };
  block: string;
  path: string;
}

const defaultState = {
  loaded: false,
  available_indexes: {},
  active_query: [],
};

const emptySubrequests = {};

/**
 * Parse URL query parameters to extract active filters and sort_on.
 * Used for SSR subrequest key matching.
 */
const parseActiveFiltersFromURL = (
  location: { search: string },
  filters: Array<{ title?: string; index: string }> | undefined,
  sortSelection:
    | Array<{ title?: string; index: string; reverse?: boolean }>
    | undefined,
  queryStringPostfix: string,
): { [key: string]: string | string[] } => {
  const parsedParams = qs.parse(location.search);
  const activeFilters: { [key: string]: string | string[] } = {};

  // Parse filter values
  if (filters?.length) {
    const postfixes = ['', '_from', '_to'];
    filters.forEach((filter) => {
      if (!filter.index) return;

      postfixes.forEach((postfix) => {
        const paramKey = `${filter.index}${postfix}${queryStringPostfix}`;
        const value = parsedParams[paramKey];

        if (value !== undefined && value !== null && value !== '') {
          // Ensure array format for filter values
          activeFilters[`${filter.index}${postfix}`] =
            postfix !== '' || Array.isArray(value) ? value : [value as string];
        }
      });
    });

    // Also check for fulltext search
    const fulltextKey = `fulltext${queryStringPostfix}`;
    if (parsedParams[fulltextKey]) {
      activeFilters['fulltext'] = parsedParams[fulltextKey] as string;
    }
  }

  // Parse sort_on from URL if sortSelection is enabled
  if (sortSelection?.length) {
    const sortOnKey = `sort_on${queryStringPostfix}`;
    if (parsedParams[sortOnKey]) {
      activeFilters['sort_on'] = parsedParams[sortOnKey] as string;
    }
  }

  return activeFilters;
};

export default function withFilter(WrappedComponent: React.ComponentType<any>) {
  function WithFilter(props: WithFilterProps) {
    const location = useLocation();
    const history = useHistory();
    const intl = useIntl();
    const queryStringPostfix = useCreateQueryStringPostfix(props.block);

    const query = [...(props.data?.querystring?.query || [])];

    // Parse active filters from URL for SSR matching - must be done before useState
    // Merged facets are collapsed to their group value so a URL that still
    // carries a member token (bookmark, shared link) ticks the group checkbox.
    // Safe for the SSR key too: expand(collapse(x)) === expand(x).
    const initialActiveFilters = collapseActiveFilters(
      parseActiveFiltersFromURL(
        location,
        props.data?.filters,
        props.data?.sortSelection,
        queryStringPostfix,
      ),
    );

    const data: DataMap = {
      ...initialActiveFilters,
    };

    if (props.data?.allowLayoutSwitch) {
      const parsedParams = qs.parse(location.search);
      const layoutFromUrl = parsedParams[`layout${queryStringPostfix}`];
      const validLayouts = ['list', 'grid'];
      const safeLayout =
        typeof layoutFromUrl === 'string' &&
        validLayouts.includes(layoutFromUrl)
          ? layoutFromUrl
          : undefined;
      data.layout = safeLayout || props.data?.variation || 'grid';
    }

    // Only set sort_on from block config if not already parsed from URL
    if (
      props.data?.sortSelection?.length &&
      props.data?.querystring?.sort_on &&
      !data.sort_on
    ) {
      data.sort_on = props.data?.querystring?.sort_on;
    }

    const [filterData, setFilterData] = useState<DataMap>(data);
    const [initialPath] = useState(getBaseUrl(props.path));
    const [initialized, setInitialized] = useState(false);
    const currentQueries = useRef<Record<string, string>>({});
    const filterKeys: string[] = [];

    // Sync filterData from URL changes (e.g. browser back/forward).
    // Must use useDeepCompareEffect (not useEffect) because `data` is a new
    // object reference every render. A plain useEffect would fire on every render,
    // causing filterData to reset to the (stale) URL-derived value before
    // history.replace() has propagated, creating a {filter} → {} → {filter} cycle.
    useDeepCompareEffect(() => {
      if (
        !deepEqual(
          filterObject(filterData, null, ['sort_on', 'layout']),
          filterObject(data, null, ['sort_on', 'layout']),
        )
      ) {
        setFilterData(data);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    // Build activeFilters for subrequest key, excluding display-only options like layout and sort_on
    // Also ensures array format for filter values to match backend expectations
    const getActiveFiltersForSubrequest = (
      filterData: DataMap,
    ): { [key: string]: string | string[] } => {
      const { layout, sort_on, ...filters } = filterData;
      // Ensure array format for filter values (except fulltext which is a string)
      const normalized: { [key: string]: string | string[] } = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (
          value === undefined ||
          value === null ||
          value === '' ||
          (Array.isArray(value) && value.length === 0)
        )
          return;
        if (key === 'fulltext') {
          normalized[key] = value as string;
        } else if (!Array.isArray(value)) {
          normalized[key] = [value as string];
        } else {
          normalized[key] = value;
        }
      });
      return expandActiveFilters(normalized);
    };

    // Build activeFilters for SSR matching, excluding sort_on to match getAsyncData.js
    const getActiveFiltersForSSR = (activeFilters: {
      [key: string]: string | string[];
    }): { [key: string]: string | string[] } => {
      const { sort_on: _sortOn, ...filters } = activeFilters;
      // Ensure array format for filter values (except fulltext which is a string)
      const normalized: { [key: string]: string | string[] } = {};
      Object.entries(filters).forEach(([key, value]) => {
        // Skip empty values including empty arrays
        if (
          value === undefined ||
          value === null ||
          value === '' ||
          (Array.isArray(value) && value.length === 0)
        )
          return;
        if (key === 'fulltext') {
          normalized[key] = value as string;
        } else if (!Array.isArray(value)) {
          normalized[key] = [value as string];
        } else {
          normalized[key] = value;
        }
      });
      return expandActiveFilters(normalized);
    };

    // The SSR subrequest includes active filters parsed from URL (excluding sort_on)
    const initialSubrequest = getSubrequest(initialPath, {
      query: query,
      filters: props.data?.filters?.map((filter) => filter.index || ''),
      activeFilters: getActiveFiltersForSSR(initialActiveFilters),
    });
    const subrequest = getSubrequest(initialPath, {
      query: query,
      filters: props.data?.filters?.map((filter) => filter.index || ''),
      activeFilters: getActiveFiltersForSubrequest(filterData),
    });

    // Check for SSR data using the initial subrequest key (with empty activeFilters)
    // Must be called before useState to use its value for initialization
    const ssrState = useSelector(
      (state: {
        availableIndexes: {
          subrequests: Record<
            string,
            {
              loaded: boolean;
              isSSR?: boolean;
              available_indexes: GetQueryStringResponse;
              active_query: Array<{
                i: string;
                o: string;
                v?: string | string[];
              }>;
            }
          >;
        };
      }) => state?.availableIndexes?.subrequests?.[initialSubrequest],
    );

    const state = useSelector(
      (state: {
        availableIndexes: {
          subrequests: Record<
            string,
            {
              loaded: boolean;
              isSSR?: boolean;
              available_indexes: GetQueryStringResponse;
              active_query: Array<{
                i: string;
                o: string;
                v?: string | string[];
              }>;
            }
          >;
        };
      }) => state?.availableIndexes?.subrequests?.[subrequest] || defaultState,
    );

    const previous_searches = useSelector(
      (state: {
        search: {
          subrequests: Record<string, { items: any[]; loaded: boolean }>;
        };
      }) => state.search?.subrequests || emptySubrequests,
    );

    // Track if we've already used SSR data
    const usedSSRRef = useRef(false);

    // Initialize from SSR state if available to avoid layout shift
    const [available_indexes, setAvailableIndexes] = useState<
      GetQueryStringResponse | undefined
    >(() => {
      if (ssrState?.loaded && ssrState?.isSSR) {
        usedSSRRef.current = true;
        return ssrState.available_indexes;
      }
      return undefined;
    });

    // Initialize from SSR state if available to avoid layout shift
    const [active_query, setActiveQuery] = useState<
      Array<{ i: string; o: string; v?: string | string[] }> | undefined
    >(() => {
      if (ssrState?.loaded && ssrState?.isSSR) {
        usedSSRRef.current = true;
        return ssrState.active_query;
      }
      return undefined;
    });

    const [resolvedSubrequest, setResolvedSubrequest] = useState<
      string | undefined
    >(() =>
      ssrState?.loaded && ssrState?.isSSR ? initialSubrequest : undefined,
    );

    useDeepCompareEffect(() => {
      // First, check if we can use SSR data (from initial subrequest with empty filters)
      if (!usedSSRRef.current && ssrState?.loaded && ssrState?.isSSR) {
        setAvailableIndexes(ssrState.available_indexes);
        setActiveQuery(ssrState.active_query);
        setResolvedSubrequest(initialSubrequest);
        usedSSRRef.current = true;
        return;
      }
      // Otherwise, use the current subrequest state
      if (state?.loaded) {
        setAvailableIndexes(state.available_indexes);
        setActiveQuery(state.active_query);
        setResolvedSubrequest(subrequest);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state, ssrState, subrequest, initialSubrequest]);

    const dispatch = useDispatch();

    useEffect(() => {
      // Skip fetching available indexes if filters are not activated
      if (!props.data?.activateFilter) {
        return;
      }
      // If SSR data is available (from initial subrequest with empty activeFilters),
      // use it and skip the dispatch to avoid double loading.
      if (!usedSSRRef.current && ssrState?.loaded && ssrState?.isSSR) {
        dispatch(availableIndexesClearSSR(initialSubrequest) as AnyAction);
        usedSSRRef.current = true;
        if (initialSubrequest === subrequest) {
          return;
        }
      }
      // If current subrequest data is already loaded from SSR, skip dispatch
      if (state?.loaded && state?.isSSR) {
        dispatch(availableIndexesClearSSR(subrequest) as AnyAction);
        return;
      }
      dispatch(
        getAvailableIndexes(initialPath, {
          query: query,
          filters: props.data?.filters?.map((filter) => filter.index || ''),
          activeFilters: getActiveFiltersForSubrequest(filterData),
        }) as AnyAction,
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subrequest]);

    const onChange = (data: DataMap) => {
      const newParams = {
        ...filterObject(qs.parse(location.search), null, filterKeys),
      };

      Object.entries(data).forEach(([key, value]) => {
        if (
          value === undefined ||
          value === null ||
          value === '' ||
          (Array.isArray(value) && value.length === 0)
        ) {
          delete newParams[`${key}${queryStringPostfix}`];
          return;
        }
        if (Array.isArray(value) && value.length > 1) {
          value.sort(); // Sort values to ensure deterministic query string
        }
        newParams[`${key}${queryStringPostfix}`] = value;
      });
      if (`page${queryStringPostfix}` in newParams) {
        delete newParams[`page${queryStringPostfix}`]; // Reset pagination on filter change
      }

      if (props.data?.allowLayoutSwitch && !data?.layout) {
        data.layout = props.data?.variation ?? 'grid';
      }

      if (
        props.data?.sortSelection?.length &&
        props.data?.querystring?.sort_on &&
        !data?.sort_on
      ) {
        data.sort_on =
          props.data?.querystring?.sort_on +
          props.data?.querystring?.sort_order_boolean
            ? '_reverse'
            : '';
      }

      setFilterData(data);

      if (
        newParams?.[`layout${queryStringPostfix}`] === props.data?.variation
      ) {
        delete newParams[`layout${queryStringPostfix}`];
      }

      if (
        newParams?.[`sort_on${queryStringPostfix}`] ===
        (props.data?.querystring?.sort_on as string) +
          (!!props.data?.querystring?.sort_order_boolean ? '_reverse' : '')
      ) {
        delete newParams[`sort_on${queryStringPostfix}`];
      }

      history.replace({
        search: qs.stringify(newParams, { sort: (a, b) => a.localeCompare(b) }),
      });
    };

    useEffect(() => {
      if (initialized || !filterKeys.length) return; // Wait until filter keys are loaded and only run once

      const params = filterObject(qs.parse(location.search), [
        ...filterKeys,
        `sort_on${queryStringPostfix}`,
        `layout${queryStringPostfix}`,
      ]);
      let changed = false;
      const data = { ...filterData };
      Object.entries(params).forEach(([key, value]) => {
        key = key.replace(queryStringPostfix, '');
        const index =
          state?.available_indexes?.indexes?.[
            key as keyof GetQueryStringResponse['indexes']
          ];

        if (
          index?.operations?.includes(
            'plone.app.querystring.operation.selection.any',
          ) &&
          !Array.isArray(value)
        ) {
          value = [value as string];
        }

        // Same collapse as on the initial parse — otherwise this effect would
        // write the raw member token back over the group value.
        value = collapseActiveFilters({ [key]: value as string | string[] })[
          key
        ];

        if (!deepEqual(data[key], value)) {
          data[key] = value;
          changed = true;
        }
      });

      if (changed) {
        setFilterData(data);
      }
      setInitialized(true);
    }, [queryStringPostfix, filterKeys]); // eslint-disable-line react-hooks/exhaustive-deps

    const filters: FilterProps = {
      items: [],
      className: 'listing__filter',
    };

    if (!props.data?.activateFilter) {
      const b_size = listingPageSize(
        props.data?.querystring?.b_size,
        props.data?.showTeaserCard,
        false,
        config.settings.defaultPageSize,
      );

      return b_size === undefined ? (
        <WrappedComponent {...props} />
      ) : (
        <WrappedComponent
          {...props}
          data={{
            ...props.data,
            querystring: {
              ...(props.data?.querystring || {}),
              b_size,
            },
          }}
        />
      );
    }

    if (props.data?.allowLayoutSwitch) {
      filters.items.push({
        label: intl.formatMessage(messages.Layout),
        showActiveCount: false,
        name: 'layout',
        values: [
          {
            label: '',
            values: [
              {
                label: intl.formatMessage(messages.List),
                value: 'list',
                name: 'layout',
                type: 'radio',
              },
              {
                label: intl.formatMessage(messages.Grid),
                value: 'grid',
                name: 'layout',
                type: 'radio',
              },
            ],
          },
        ],
        apply: intl.formatMessage(messages.Apply),
        close: intl.formatMessage(messages.Close),
      });

      if (props?.data?.variation === 'grid') {
        filters.items[0].values?.[0].values?.reverse();
      }
    }

    if (props?.data?.sortSelection?.length) {
      const items: DropdownItem[] = [];
      if (props?.data?.querystring?.sort_on) {
        items.push({
          label:
            available_indexes?.sortable_indexes?.[
              props.data.querystring
                .sort_on as keyof GetQueryStringResponse['sortable_indexes']
            ]?.title || '',
          value:
            (props.data.querystring.sort_on as string) +
            (props.data.querystring.sort_order_boolean ? '_reverse' : ''),
          name: 'sort_on',
          type: 'radio',
        });
      }
      props.data.sortSelection.forEach((selection) => {
        if (!selection.index) return;
        const index =
          available_indexes?.sortable_indexes?.[
            selection.index as keyof GetQueryStringResponse['sortable_indexes']
          ];
        if (!index) return null;
        const value = selection.index + (selection.reverse ? '_reverse' : '');
        if (props?.data?.querystring?.sort_on && value === items[0]?.value) {
          items.shift(); // Default sort item already added
        }
        items.push({
          label: selection.title || index.title,
          value: value,
          name: 'sort_on',
          type: 'radio',
        });
      });

      if (items.length) {
        if (filters.items.length && filters.items?.[0]?.values?.length) {
          filters.items[0].values[0].label = filters.items[0].label;
          filters.items[0].values?.push({
            label: intl.formatMessage(messages.SortOrder),
            values: items,
          });
          filters.items[0].label = intl.formatMessage(
            messages.LayoutAndSortOrder,
          );
        } else {
          filters.items.push({
            label: intl.formatMessage(messages.SortOrder),
            showActiveCount: false,
            name: 'sort',
            values: [
              {
                label: '',
                values: items,
              },
            ],
            apply: intl.formatMessage(messages.Apply),
            close: intl.formatMessage(messages.Close),
          });
        }
      }
    }

    props?.data?.filters?.forEach((filter) => {
      if (!filter.index) return;
      const index =
        available_indexes?.indexes?.[
          filter.index as keyof GetQueryStringResponse['indexes']
        ];
      if (!index) return;

      if (filter.index === 'SearchableText') {
        filters['fulltext'] =
          filter.title || intl.formatMessage(messages.Search);
        filterKeys.push(`fulltext${queryStringPostfix}`);
        return;
      }

      if (
        index?.operations?.includes(
          'plone.app.querystring.operation.selection.any',
        )
      ) {
        filterKeys.push(`${filter.index}${queryStringPostfix}`);

        if (
          index.vocabulary &&
          index.vocabulary.startsWith('fhnw.vocabularies.content.') &&
          TYPEAHEAD_FILTERS.includes(filter.index)
        ) {
          // Both remaining criteria name their content type directly
          // ("...content.DegreeProgram"). The two vocabularies that did not -
          // Product, and Contact_TeamContact, which searched for a content type
          // of that literal name and therefore never found anything - are value
          // lists now and no longer reach this branch (#1777).
          const portal_type: string = index.vocabulary.replace(
            'fhnw.vocabularies.content.',
            '',
          );
          filters.items.push({
            label: filter.title || index.title,
            name: filter.index,
            search: intl.formatMessage(messages.Search),
            searchSelected: intl.formatMessage(messages.Active),
            searchResults: intl.formatMessage(messages.SearchResults),
            searchNoResults: intl.formatMessage(messages.NoResultsFound),
            searchIndex: filter.index,
            showMore: intl.formatMessage(messages.More),
            onSearch: async (query) => {
              currentQueries.current[filter.index] = query;
              const promise = new Promise<
                { label: string; value: string; name: string }[]
              >((resolve) => {
                if (currentQueries.current[filter.index] !== query) {
                  return;
                }

                if (!query || query.toLowerCase() === 'nothing') {
                  resolve([]);
                  return;
                }

                const subrequest = `${filter.index}_${portal_type}`;

                if (previous_searches?.[subrequest]?.loaded) {
                  resolve(
                    previous_searches[subrequest].items.map((item) => ({
                      label: item?.name_affix1
                        ? `${item.name_affix1} ${item.Title}`
                        : item.Title,
                      value: item.UID,
                      name: filter.index,
                    })),
                  );
                }

                dispatch(
                  searchContent(
                    '/',
                    {
                      SearchableText: query,
                      Language: intl.locale,
                      portal_type: portal_type,
                      sort_on: 'relevance',
                      metadata_fields: ['Title', 'UID', 'name_affix1'],
                    },
                    subrequest,
                  ) as any,
                )
                  .then(
                    ({
                      items,
                    }: {
                      items: Array<{
                        Title: string;
                        UID: string;
                        name_affix1?: string;
                      }>;
                    }) => {
                      if (currentQueries.current[filter.index] !== query) {
                        return;
                      }
                      resolve(
                        items.map((item) => ({
                          label: item?.name_affix1
                            ? `${item.name_affix1} ${item.Title}`
                            : item.Title,
                          value: item.UID,
                          name: filter.index,
                        })),
                      );
                    },
                  )
                  .catch(() => {
                    if (currentQueries.current[filter.index] !== query) {
                      return;
                    }
                    resolve([]);
                  });
              });

              return promise;
            },
            getSearchTerm: async (value) => {
              const promise = new Promise<{
                label: string;
                value: string;
                name: string;
              } | null>((resolve) => {
                if (!value) {
                  resolve(null);
                  return;
                }

                const subrequest = `${filter.index}_${value}`;

                if (previous_searches?.[subrequest]?.loaded) {
                  const items = previous_searches[subrequest].items;
                  if (!items.length) {
                    resolve(null);
                    return;
                  }
                  resolve({
                    label: items[0]?.name_affix1
                      ? `${items[0].name_affix1} ${items[0].Title}`
                      : items[0].Title,
                    value: items[0].UID,
                    name: filter.index,
                  });
                  return;
                }

                dispatch(
                  searchContent(
                    '/',
                    {
                      UID: value,
                      Language: intl.locale,
                      portal_type: portal_type,
                      metadata_fields: ['Title', 'UID', 'name_affix1'],
                    },
                    subrequest,
                  ) as any,
                )
                  .then(
                    ({
                      items,
                    }: {
                      items: Array<{
                        Title: string;
                        UID: string;
                        name_affix1?: string;
                      }>;
                    }) => {
                      if (!items.length) {
                        resolve(null);
                        return;
                      }
                      resolve({
                        label: items[0]?.name_affix1
                          ? `${items[0].name_affix1} ${items[0].Title}`
                          : items[0].Title,
                        value: items[0].UID,
                        name: filter.index,
                      });
                    },
                  )
                  .catch(() => {
                    resolve(null);
                  });
              });

              return promise;
            },
            apply: intl.formatMessage(messages.Apply),
            close: intl.formatMessage(messages.Close),
            reset: intl.formatMessage(messages.Reset),
          });

          // A type-ahead criterion is served by the search alone. Adding a
          // value list on top would give the same filter name two conflicting
          // entries, which Filter cannot map (#1777).
          return;
        }

        if (!index.values || !Object.keys(index.values).length) return;
        const grouped = GROUPED_FILTERS.includes(filter.index);
        const groupMap: Record<string, DropdownItem> = {};
        const groupLabels: Record<string, string> = {};

        let values: Array<DropdownItem> = [];
        Object.entries(
          index.values as Record<
            string,
            { title: string; token: string; count: number }
          >,
        ).forEach(([val, option]) => {
          if (option.count === 0 && !grouped) return;
          const value: DropdownItem = {
            type: 'checkbox',
            label: option.title,
            value: val,
            name: filter.index,
          };

          if (grouped) {
            if (option.token && option.token.includes('_')) {
              const parents = option.token.split('_');
              parents.pop(); // Remove self
              parents.forEach((parent) => {
                if (!(parent in groupMap)) {
                  groupMap[parent] = {
                    label: groupLabels?.[parent] || '',
                    values: [],
                  };
                  values.push(groupMap[parent]);
                } else if (!('values' in groupMap[parent])) {
                  groupMap[parent].values = [];
                }
                if (option.count === 0) return;
                groupMap[parent]?.values?.push(value);
              });
              return;
            } else if (val in groupMap) {
              groupMap[val].type = value.type;
              groupMap[val].label = value.label;
              groupMap[val].value = val;
              groupMap[val].name = filter.index;
              return;
            }
            groupLabels[val] = option.title;
            if (option.count === 0) return;
            groupMap[val] = value;
          }
          values.push(value);
        });

        if (grouped) {
          values = values.filter((v) => !('values' in v) || v.values?.length);
        }

        values = collapseFacetForDisplay(values, filter.index, intl);

        if (values.length === 0) return;

        sortFilterValues(values, filter.index);

        filters.items.push({
          label: filter.title || index.title,
          name: filter.index,
          values: grouped
            ? values
            : [
                {
                  label: filter.title || index.title,
                  values: values,
                },
              ],
          selectAll: intl.formatMessage(messages.SelectAll),
          apply: intl.formatMessage(messages.Apply),
          close: intl.formatMessage(messages.Close),
          reset: intl.formatMessage(messages.Reset),
          search: ENABLE_SEARCH.includes(filter.index)
            ? intl.formatMessage(messages.Search)
            : undefined,
        });
      } else if (
        index?.operations?.includes(
          'plone.app.querystring.operation.date.between',
        )
      ) {
        filterKeys.push(
          `${filter.index}_from${queryStringPostfix}`,
          `${filter.index}_to${queryStringPostfix}`,
        );

        filters.items.push({
          label: filter.title || index.title,
          values: [
            {
              label: '',
              values: [
                {
                  type: 'daterange',
                  label: {
                    from: intl.formatMessage(messages.From),
                    to: intl.formatMessage(messages.To),
                  },
                  name: filter.index,
                },
              ],
            },
          ],
          apply: intl.formatMessage(messages.Apply),
          close: intl.formatMessage(messages.Close),
          reset: intl.formatMessage(messages.Reset),
        });
      }
    });

    // While a relevance-triggering filter (e.g. fulltext) is active,
    // override the configured sort field with the systems relevance ranking.
    // Derived from the applied filters, not from active_query: the latter also
    // holds the block's own base query, so a listing that is permanently
    // scoped to a topic would lose its configured sort order - and only in the
    // client, since getAsyncData.js reads the filters during SSR.
    const useRelevanceSort = hasRelevanceTrigger(filterData);

    // Build the modified data with active_query (includes exclude_from_filter_pages)
    // This ensures consistent query params even during early returns
    const modifiedData = {
      ...props.data,
      filterPending:
        !!props.data?.activateFilter && resolvedSubrequest !== subrequest,
      variation: undefined,
      template: filterData.layout || props.data?.variation || 'grid',
      querystring: {
        ...(props.data?.querystring || {}),
        query: active_query || query,
        sort_on: useRelevanceSort
          ? 'relevance'
          : filterData.sort_on
            ? (filterData.sort_on as string).replace(/_reverse$/, '')
            : props.data?.querystring?.sort_on,
        sort_order: (
          filterData.sort_on
            ? (filterData.sort_on as string).match(/.*_reverse$/) !== null
            : props.data?.querystring?.sort_order_boolean
        )
          ? 'descending'
          : 'ascending',
        b_size: listingPageSize(
          props.data?.querystring?.b_size,
          props.data?.showTeaserCard,
          true,
          config.settings.defaultPageSize,
        ),
      },
    };

    const showFilter = !!(filters?.items?.length || filters?.fulltext);

    // Always render the same JSX structure to prevent React from
    // unmounting/remounting WrappedComponent when filters load
    return (
      <>
        {showFilter && (
          <Filter
            {...filters}
            data={filterData}
            onChange={onChange}
            activeFiltersBlacklist={['layout', 'sort_on']}
            reset={intl.formatMessage(messages.Reset)}
            back={intl.formatMessage(messages.Back)}
            showResults={intl.formatMessage(messages.ShowResults)}
            triggerIcon={<Icon name={filterSVG} size="16px" />}
            logo={
              {
                href: `/${intl.locale}`,
                logo: {
                  alt: 'FHNW',
                  src: `/logo/fhnw-logo-${intl.locale === 'de' ? 'de' : 'en'}.svg`,
                },
              } as LogoProps
            }
          />
        )}
        <WrappedComponent {...props} data={modifiedData} />
      </>
    );
  }

  WithFilter.displayName = `WithFilter(${getDisplayName(WrappedComponent)})`;

  return hoistNonReactStatics(WithFilter, WrappedComponent);
}
